import type { ColunaTetoLateral, IdZonaTetoLateral } from '../../dominio/didatico/rebatimentoLateralTeto'
import { montanteColunaC, xDaEstruturaNaAltura, zDaColunaNaAltura, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'

/** Variante didática com quatro montantes: C junto ao vão traseiro e D no extremo do teto. */
export function pontosTetoLateral(perfil: PerfilVeiculo) {
  const c = montanteColunaC(perfil)
  const colunas: Record<ColunaTetoLateral, { zBase: number; zTopo: number }> = {
    A: perfil.colunaA,
    B: { zBase: perfil.colunaB.z, zTopo: perfil.colunaB.z },
    C: { zBase: c.zBase, zTopo: perfil.teto.zTraseiro + 0.28 },
    D: { zBase: perfil.colunaC.zBase, zTopo: perfil.teto.zTraseiro },
  }
  const yCorte = perfil.tetoY - 0.14
  const pontoColuna = (coluna: ColunaTetoLateral, sinal: number, y: number): [number, number, number] => [xDaEstruturaNaAltura(perfil, sinal, y), y, zDaColunaNaAltura(perfil, colunas[coluna], y)]
  const xInferior = xDaEstruturaNaAltura(perfil, 1, perfil.tetoY)
  const centroZ = (perfil.teto.zDianteiro + perfil.teto.zTraseiro) / 2
  const pivo: [number, number, number] = [xInferior, perfil.tetoY, centroZ]
  const vidroInicio = pontoColuna('A', -1, yCorte)
  const vidroFim = pontoColuna('A', 1, yCorte)
  const alivioA: [number, number, number] = [xInferior, perfil.tetoY, perfil.teto.zDianteiro - 0.10]
  const alivioD: [number, number, number] = [xInferior, perfil.tetoY, perfil.teto.zTraseiro + 0.10]
  return { colunas, yCorte, pontoColuna, xInferior, centroZ, pivo, vidroInicio, vidroFim, alivioA, alivioD }
}
export function pontoRebatidoLateral(perfil: PerfilVeiculo, ponto: [number, number, number], progresso: number): [number, number, number] {
  const { pivo } = pontosTetoLateral(perfil)
  const angulo = -Math.PI / 2 * Math.max(0, Math.min(1, progresso))
  const dx = ponto[0] - pivo[0], dy = ponto[1] - pivo[1]
  return [pivo[0] + dx * Math.cos(angulo) - dy * Math.sin(angulo), pivo[1] + dx * Math.sin(angulo) + dy * Math.cos(angulo), ponto[2]]
}
export function zonaTetoLateralPorId(perfil: PerfilVeiculo, id: IdZonaTetoLateral, progressoVidro = 0, progressoRebatimento = 0) {
  const p = pontosTetoLateral(perfil)
  const posicoes: Record<IdZonaTetoLateral, [number, number, number]> = {
    tetoColunaA: p.pontoColuna('A', -1, p.yCorte), tetoColunaB: p.pontoColuna('B', -1, p.yCorte),
    tetoColunaC: p.pontoColuna('C', -1, p.yCorte), tetoColunaD: p.pontoColuna('D', -1, p.yCorte),
    tetoParabrisa: p.vidroInicio.map((n, i) => n + (p.vidroFim[i] - n) * Math.max(0, Math.min(1, progressoVidro))) as [number, number, number],
    tetoAlivioA: p.alivioA, tetoAlivioD: p.alivioD,
    tetoRebater: pontoRebatidoLateral(perfil, [0, perfil.tetoY, p.centroZ], progressoRebatimento),
  }
  return { id, posicao: posicoes[id], anguloGraus: id.startsWith('tetoColuna') ? 90 : 0 }
}

/** Câmera do lado do teto; todos os pontos usam a rolagem de -90° da maquete tombada. */
export function enquadramentoTetoLateral(perfil: PerfilVeiculo, ponto?: [number, number, number]) {
  const p = pontosTetoLateral(perfil)
  const centro = ponto ?? [p.xInferior / 2, perfil.tetoY + p.xInferior / 2, p.centroZ]
  const alvo: [number, number, number] = [centro[1], perfil.largura / 2 - centro[0], centro[2]]
  const distancia = ponto ? [3.5, 2.0, 2.4] : [5.8, 3.4, 4.6]
  return { alvo, posicao: alvo.map((n, i) => n + distancia[i]) as [number, number, number] }
}

/** Fendas longitudinais: Z permanece horizontal depois da rolagem do carro tombado. */
export function cortesAlivioTeto(perfil: PerfilVeiculo, alivioA: boolean, alivioD: boolean) {
  const p = pontosTetoLateral(perfil)
  return [
    ...(alivioD ? [{ de: perfil.teto.zTraseiro, ate: perfil.teto.zTraseiro + 0.20 }] : []),
    ...(alivioA ? [{ de: perfil.teto.zDianteiro - 0.20, ate: perfil.teto.zDianteiro }] : []),
  ].map((corte) => ({ ...corte, x: p.xInferior, largura: 0.024 }))
}

/** Reforço inferior interrompido nas mesmas duas fendas horizontais da chapa. */
export function trechosLongarinaTetoInferior(perfil: PerfilVeiculo, alivioA: boolean, alivioD: boolean): Array<[number, number]> {
  const cortes = cortesAlivioTeto(perfil, alivioA, alivioD)
  const trechos: Array<[number, number]> = []
  let inicio = perfil.teto.zTraseiro
  for (const corte of cortes) {
    if (corte.de > inicio) trechos.push([inicio, corte.de])
    inicio = corte.ate
  }
  if (inicio < perfil.teto.zDianteiro) trechos.push([inicio, perfil.teto.zDianteiro])
  return trechos
}
